import type { ApolloQueryResult, OperationVariables, QueryOptions } from '@apollo/client'

import {
  FieldTaxonomyTermParent,
  LanguageId,
  TaxonomyTermFieldsFragment,
  TermsDocument,
  TermsQuery,
  TermsQueryVariables,
} from '../graphql/generated'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'

type ApolloQueryFn = <TData, TVariables extends OperationVariables>(
  options: QueryOptions<TVariables, TData>,
) => Promise<ApolloQueryResult<TData>>

export const TERM_VOCABULARIES = [
  'visible_tags',
  'event_info_type',
  'tocco_event_category',
  'tocco_supply_area',
  'tocco_target_group',
  'tocco_event_qualification',
  'cbz_course_category',
  'cbz_education',
  'cbz_region',
  'institution_sector',
  'event_category',
  'event_type',
] as const
export type TermVocabulary = (typeof TERM_VOCABULARIES)[number]

export interface Term {
  id: string
  name: string
  parent: string
}

export type Terms = Record<TermVocabulary, Term[]>

const createEmptyTerms = (): Terms =>
  TERM_VOCABULARIES.reduce(
    (acc, key) => {
      acc[key] = []
      return acc
    },
    {} as Record<TermVocabulary, Term[]>,
  )

const hasParentField = (
  term: TaxonomyTermFieldsFragment,
): term is TaxonomyTermFieldsFragment & { parent?: (FieldTaxonomyTermParent | null)[] | null } => 'parent' in term

const isParentField = (value: FieldTaxonomyTermParent | null | undefined): value is FieldTaxonomyTermParent =>
  Boolean(value)

const getTerms = async (
  query: ApolloQueryFn | null | undefined,
  vocabularies: TermVocabulary[],
  parents: string[],
  language: LanguageId,
): Promise<Terms> => {
  if (!query) {
    return createEmptyTerms()
  }

  const response = await Promise.allSettled(
    vocabularies.map(async (vocabulary) => {
      const variables: TermsQueryVariables = {
        vocabulary,
        parentId: parents,
        language,
        ...(vocabulary === 'institution_sector' ? { limit: 50 } : {}),
      }
      const { data } = await query<TermsQuery, TermsQueryVariables>({
        query: TermsDocument,
        variables,
      })
      if (!data) return null

      const termsForVocabulary =
        (data.taxonomyTermQuery?.entities as TaxonomyTermFieldsFragment[] | undefined)?.map((item) => {
          const parentField = hasParentField(item) ? item.parent?.find(isParentField) : undefined

          return {
            id: item.tid?.toString() ?? '',
            name: item.name ?? '',
            parent: parentField?.targetId != null ? parentField.targetId.toString() : '',
          }
        }) ?? []

      return [vocabulary, termsForVocabulary] as const
    }),
  )

  const sucessfulRequests = response.filter((res) => res.status === 'fulfilled') as {
    status: 'fulfilled'
    value: [TermVocabulary, Term[]]
  }[]
  const terms = sucessfulRequests
    .map((res) => res.value)
    .reduce((acc, cur) => ({ ...acc, [cur[0]]: cur[1] }), createEmptyTerms())

  return terms
}

export const useTerms = async (vocabularies: TermVocabulary[], parents: string[] = ['0']) => {
  const { languageCODE } = useLocale()
  const clients = useTypedApolloClients()

  const cacheKey = ['terms', languageCODE.value, ...vocabularies, ...parents].filter(Boolean).join('-')

  // Use useAsyncData for initial SSR data fetch
  const { data: initialTerms } = await useAsyncData(cacheKey, async () => {
    return await getTerms(clients?.default.query, vocabularies, parents, languageCODE.value as LanguageId)
  })

  const terms = ref(initialTerms.value ?? createEmptyTerms())
  const active = reactive(
    vocabularies.reduce(
      (acc, cur) => ({
        ...acc,
        [cur]: [],
      }),
      {} as Record<TermVocabulary, string[]>,
    ),
  )

  return { terms, active } as const
}
